import { runInAction, makeObservable, observable, action } from 'mobx';
import BaseViewModel from './BaseViewModel';
import { NormalizationEnum } from '../constants/normalizations';
import { Attribute } from '../models/Attribute';

export enum AttributesBusyStateKey {
  FETCH_ATTRIBUTES = 'fetchAttributes',
  SAVE_ATTRIBUTE = 'saveAttribute',
  DELETE_ATTRIBUTE = 'deleteAttribute',
}

export default class AttributesPageViewModel extends BaseViewModel<AttributesBusyStateKey> {
  attributes: Attribute[] = [];
  currentAttribute: Attribute = { name: '', description: '', normalization: NormalizationEnum.NONE, is_outcome: false };

  constructor() {
    super();

    Object.values(AttributesBusyStateKey).forEach(key => {
      this.isLoading[key] = false;
    });

    makeObservable(this, {
      attributes: observable,
      currentAttribute: observable,
      createAttribute: action,
      fetchAttributes: action,
      setCurrentAttribute: action,
      saveAttribute: action,
      deleteAttribute: action,
      resetCurrentAttribute: action,
    });
  }

  async createAttribute(attribute: Attribute) {
    return this.api.post('/attributes', attribute)
      .then(() => this.fetchAttributes());
  }

  async fetchAttributes() {
    runInAction(() => {
      this.isLoading[AttributesBusyStateKey.FETCH_ATTRIBUTES] = true;
      this.error = null;
    });

      this.api.get<Attribute[]>('/attributes')
      .then((response) => {
        runInAction(() => {
          this.attributes = response.data.sort((a, b) => a.name.localeCompare(b.name));
        });
      })
      .catch(() => {
        runInAction(() => {
          this.error = 'Failed to fetch attributes. Please try again later.';
        });
      })
      .finally(() => {
        runInAction(() => {
          this.isLoading[AttributesBusyStateKey.FETCH_ATTRIBUTES] = false;
        });
      });
  }

  setCurrentAttribute(attribute: Attribute | null) {
    this.currentAttribute = attribute || { name: '', description: '', normalization: NormalizationEnum.NONE, is_outcome: false };
  }  

  async saveAttribute(attribute: Attribute) {
    runInAction(() => {
      this.isLoading[AttributesBusyStateKey.SAVE_ATTRIBUTE] = true;
    });

    return (attribute.id 
      ? this.api.put(`/attributes/${attribute.id}`, attribute)
      : this.api.post('/attributes', attribute))
      .then(() => {
        this.fetchAttributes();
      })
      .then(() => {
        this.resetCurrentAttribute();
      })
      .finally(() => {
        runInAction(() => {
          this.isLoading[AttributesBusyStateKey.SAVE_ATTRIBUTE] = false;
        });
      });
  }

  async deleteAttribute(id: string) {
    runInAction(() => {
      this.isLoading[AttributesBusyStateKey.DELETE_ATTRIBUTE] = true;
    });

    return this.api.delete(`/attributes/${id}`)
      .then(() => this.fetchAttributes())
      .finally(() => {
        runInAction(() => {
          this.isLoading[AttributesBusyStateKey.DELETE_ATTRIBUTE] = false;
        });
      });
  }

  resetCurrentAttribute() {
    this.currentAttribute = { name: '', description: '', normalization: NormalizationEnum.NONE, is_outcome: false };
  }
}